import { ref, shallowRef, computed, watch, getCurrentScope, onScopeDispose } from 'vue';
import { ShipmentService } from '../application/services/ShipmentService';
import { FirestoreShipmentRepository } from '../infrastructure/repositories/FirestoreShipmentRepository';
import { useCompanyStore } from '../stores/company';
import { IContainer } from '@/domain/entities/Container';
import { collection, onSnapshot, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { FirebaseProvider } from '../infrastructure/providers/FirebaseProvider';
import { useAuthStore } from '../stores/auth';
import { simpleCache } from '../utils/simpleCache'; // ✅ OPTIMIZACIÓN: Caché
import { useAssignedShipmentView } from './useAssignedShipmentView';


interface INotification {
  id: string;
  createdAt: any;
  description: string;
  field: string;
  newValue: any;
  oldValue: any;
  usersNotification: string[];
  usersView: string[];
  message?: string;
  shipmentId?: string;
}

export function useShipments() {
  const shipmentService = new ShipmentService(new FirestoreShipmentRepository());
  const companyStore = useCompanyStore();
  const db = FirebaseProvider.getInstance().getFirestore();
  const authStore = useAuthStore();

  const shipments = shallowRef<any[]>([]);
  const shipmentsContainers = shallowRef<Record<string, IContainer[]>>({});
  const shipmentsNotifications = shallowRef<Record<string, INotification[]>>({});
  const shipmentsItems = shallowRef<Record<string, any[]>>({});
  const shipmentsCustomsDeclarations = shallowRef<Record<string, any[]>>({});

  // Señal de invalidación: se incrementa cuando el listener detecta cambios en Firestore.
  // Útil para que el composable de BigQuery sepa cuándo re-fetch.
  const dataInvalidatedSignal = ref(0);

  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const totalItems = ref(0);
  const currentPage = ref(1);
  // ✅ OPTIMIZACIÓN: pageSize para paginación UI (25 items por página)
  const pageSize = ref(25);

  const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));

  const fetchShipments = async (filters = {}, force = false) => {
    try {
      isLoading.value = true;
      error.value = null;

      // Vista asignada: el usuario restringido no carga embarques desde Firestore.
      // Sus datos vienen exclusivamente de BigQuery con los filtros de la vista
      // (ver useAssignedShipmentView y ShipmentTable.fetchBigQueryData).
      const { loadAssignedView, hasAssignedView } = useAssignedShipmentView();
      await loadAssignedView(companyStore.selectedCompanyId || '', authStore.user?.uid || '', {
        isAdmin: authStore.isAdminOrSuperAdmin,
      });
      if (hasAssignedView.value) {
        shipments.value = [];
        totalItems.value = 0;
        return;
      }
      
      // Convert filters to plain object
      const plainFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        // Only include non-empty values
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      // ✅ OPTIMIZACIÓN: Generar clave de caché única
      const cacheKey = `shipments_${companyStore.selectedCompanyId}_${JSON.stringify(plainFilters)}`;
      
      // ✅ Si force=true, invalidar el caché antes de consultar
      if (force) {
        simpleCache.clear(`shipments_${companyStore.selectedCompanyId}`)
      }
      
      // ✅ OPTIMIZACIÓN: Verificar caché primero (solo si no es force)
      if (!force) {
        const cachedData = simpleCache.get(cacheKey);
        if (cachedData) {
          shipments.value = cachedData.items;
          totalItems.value = cachedData.total;
          isLoading.value = false;
          return;
        }
      }
      
      const result = await shipmentService.getShipments(
        plainFilters,
        1, // Siempre página 1 en backend (traemos todos)
        500, // Traer hasta 500 items
        companyStore.selectedCompanyId
      );
      
      shipments.value = result.items;
      totalItems.value = result.total;
      
      // ✅ OPTIMIZACIÓN: Guardar en caché (5 minutos)
      simpleCache.set(cacheKey, { 
        items: result.items, 
        total: result.total 
      }, 5 * 60 * 1000);
      
      // ✅ OPTIMIZACIÓN: NO cargar containers/notifications automáticamente
      // Esto elimina las 401 queries. ShipmentTable hará paginación local.
      // Si necesitas containers/notifications, crear función loadShipmentDetails(id)
      
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Error loading shipments';
      console.error('❌ [useShipments] Error:', e);
    } finally {
      isLoading.value = false;
    }
  };

  const setPage = (page: number) => {
    currentPage.value = page;
  };

  /**
   * ✅ OPTIMIZACIÓN: Cargar TODOS los containers usando collectionGroup
   * collectionGroup permite consultar TODAS las subcolecciones con el mismo nombre
   * en una sola query, independientemente de su ubicación en la jerarquía
   * 
   * Estructura Firestore:
   * Companies/{companyId}/shipments/{shipmentId}/containers/{containerId}
   * 
   * collectionGroup('containers') trae TODOS los containers de TODOS los shipments
   * ⚠️ IMPORTANTE: Requiere crear índice compuesto en Firestore Console
   */
  const loadAllContainers = async () => {
    if (!companyStore.selectedCompanyId) return;
    
    try {
      // Verificar caché primero
      const cacheKey = `containers_${companyStore.selectedCompanyId}`;
      const cachedData = simpleCache.get(cacheKey);
      
      // Solo usar caché si tiene datos reales, no un objeto vacío
      if (cachedData && Object.keys(cachedData).length > 0) {
        shipmentsContainers.value = cachedData;
        return;
      }
      
      
      
      // ⚠️ Alternativa: Batch queries en paralelo (más rápido que secuencial)
      // Como collectionGroup requiere índice especial, usamos Promise.all para paralelizar
      const shipmentIds = shipments.value.map((s: any) => s.id);
      
      const containersPromises = shipmentIds.map(async (shipmentId) => {
        const containersRef = collection(
          db, 
          'Companies', 
          companyStore.selectedCompanyId, 
          'shipments', 
          shipmentId, 
          'containers'
        );
        const containersSnapshot = await getDocs(containersRef);
        return {
          shipmentId,
          containers: containersSnapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
          }))
        };
      });
      
      // ✅ Ejecutar TODAS las queries en paralelo (mucho más rápido)
      const results = await Promise.all(containersPromises);
      
      // Agrupar containers por shipmentId
      const containersMap: Record<string, IContainer[]> = {};
      results.forEach(({ shipmentId, containers }) => {
        containersMap[shipmentId] = containers as IContainer[];
      });
      
      shipmentsContainers.value = containersMap;
      
      const totalContainers = Object.values(containersMap).reduce((sum, arr) => sum + arr.length, 0);
      
      // Solo guardar en caché si hay datos
      if (Object.keys(containersMap).length > 0) {
        simpleCache.set(cacheKey, containersMap, 5 * 60 * 1000);
      } else {
      }
      
    } catch (error) {
      console.error('❌ [useShipments] Error cargando containers:', error);
    }
  };

  /**
   * ✅ Cargar TODAS las notificaciones usando Promise.all en paralelo
   * Similar a loadAllContainers pero para notificaciones
   *
   * Con `shipmentIds` carga solo esos embarques (la página que la tabla tiene en
   * mano) y los mezcla en el mapa sin pasar por el caché, que es de la lista
   * completa. Sin ids recorre la lista Firestore: una consulta por embarque.
   */
  const loadAllNotifications = async (onlyShipmentIds?: string[]) => {
    if (!companyStore.selectedCompanyId) return;
    const isPartial = onlyShipmentIds !== undefined;
    
    try {
      // Verificar caché primero
      const cacheKey = `notifications_${companyStore.selectedCompanyId}`;
      const cachedData = isPartial ? null : simpleCache.get(cacheKey);
      
      if (cachedData) {
        shipmentsNotifications.value = cachedData;
        return;
      }
      
      const shipmentIds = onlyShipmentIds ?? shipments.value.map((s: any) => s.id);
      // Sin embarques no hay nada que pedir, y no hay que borrar lo ya cargado
      if (shipmentIds.length === 0) return;
      

      const notificationsPromises = shipmentIds.map(async (shipmentId) => {
        const notificationsRef = collection(
          db, 
          'Companies', 
          companyStore.selectedCompanyId, 
          'shipments', 
          shipmentId, 
          'notifications'
        );
        
        // Obtener TODAS las notificaciones del embarque
        const userId = authStore.user?.uid;
        const notificationsSnapshot = await getDocs(notificationsRef);
        
        // Mapear todas las notificaciones y agregar el campo isUnviewed
        const notifications = notificationsSnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              // Agregar campo computed isUnviewed basado en si el usuario está en usersView
              isUnviewed: userId ? (data.usersView?.includes(userId) || false) : false
            };
          })
          .filter((notification: any) => {
            // Filtrar solo notificaciones donde el usuario está involucrado
            if (!userId) return false;
            return (
              notification.usersNotification?.includes(userId) ||
              notification.usersView?.includes(userId) ||
              notification.users?.includes(userId)
            );
          });
        
        return {
          shipmentId,
          notifications
        };
      });
      
      // ✅ Ejecutar TODAS las queries en paralelo
      const results = await Promise.all(notificationsPromises);
      
      // Agrupar notificaciones por shipmentId (parcial: conservar las demás páginas)
      const notificationsMap: Record<string, INotification[]> = isPartial
        ? { ...shipmentsNotifications.value }
        : {};
      results.forEach(({ shipmentId, notifications }) => {
        notificationsMap[shipmentId] = notifications as INotification[];
      });
      
      shipmentsNotifications.value = notificationsMap;
      
      // Guardar en caché (5 minutos) solo la carga completa
      if (!isPartial) simpleCache.set(cacheKey, notificationsMap, 5 * 60 * 1000);

      
    } catch (error) {
      console.error('❌ [useShipments] Error cargando notificaciones:', error);
    }
  };

  /**
   * ✅ Cargar TODOS los items usando Promise.all en paralelo
   * Similar a loadAllContainers pero para items/productos
   */
  const loadAllItems = async () => {
    if (!companyStore.selectedCompanyId) return;
    
    try {
      // Verificar caché primero
      const cacheKey = `items_${companyStore.selectedCompanyId}`;
      const cachedData = simpleCache.get(cacheKey);
      
      // Solo usar caché si tiene datos reales, no un objeto vacío
      if (cachedData && Object.keys(cachedData).length > 0) {
        shipmentsItems.value = cachedData;
        return;
      }
      
      
      // Batch queries en paralelo (similar a contenedores)
      const shipmentIds = shipments.value.map((s: any) => s.id);
      
      const itemsPromises = shipmentIds.map(async (shipmentId) => {
        const itemsRef = collection(
          db, 
          'Companies', 
          companyStore.selectedCompanyId, 
          'shipments', 
          shipmentId, 
          'items'
        );
        const itemsSnapshot = await getDocs(itemsRef);
        return {
          shipmentId,
          items: itemsSnapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
          }))
        };
      });
      
      // ✅ Ejecutar TODAS las queries en paralelo (mucho más rápido)
      const results = await Promise.all(itemsPromises);
      
      // Agrupar items por shipmentId
      const itemsMap: Record<string, any[]> = {};
      results.forEach(({ shipmentId, items }) => {
        itemsMap[shipmentId] = items;
      });
      
      shipmentsItems.value = itemsMap;
      
      const totalItems = Object.values(itemsMap).reduce((sum, arr) => sum + arr.length, 0);
      
      // Solo guardar en caché si hay datos
      if (Object.keys(itemsMap).length > 0) {
        simpleCache.set(cacheKey, itemsMap, 5 * 60 * 1000);
      } else {
      }
      
    } catch (error) {
      console.error('❌ [useShipments] Error cargando items:', error);
    }
  };

  const loadAllCustomsDeclarations = async () => {
    if (!companyStore.selectedCompanyId) return;

    try {
      const cacheKey = `customs_${companyStore.selectedCompanyId}`;
      const cachedData = simpleCache.get(cacheKey);

      if (cachedData && Object.keys(cachedData).length > 0) {
        shipmentsCustomsDeclarations.value = cachedData;
        return;
      }

      const shipmentIds = shipments.value.map((s: any) => s.id);

      const customsPromises = shipmentIds.map(async (shipmentId) => {
        const customsRef = collection(
          db,
          'Companies',
          companyStore.selectedCompanyId,
          'shipments',
          shipmentId,
          'customs'
        );
        const customsSnapshot = await getDocs(customsRef);
        return {
          shipmentId,
          declarations: customsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
              updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
            };
          })
        };
      });

      const results = await Promise.all(customsPromises);

      const customsMap: Record<string, any[]> = {};
      results.forEach(({ shipmentId, declarations }) => {
        customsMap[shipmentId] = declarations;
      });

      shipmentsCustomsDeclarations.value = customsMap;

      if (Object.keys(customsMap).length > 0) {
        simpleCache.set(cacheKey, customsMap, 5 * 60 * 1000);
      }

    } catch (error) {
      console.error('❌ [useShipments] Error cargando customs declarations:', error);
    }
  };

  /**
   * ✅ OPTIMIZACIÓN: Cargar containers solo de los shipments visibles en la página actual
   * Alternativa más ligera cuando hay muchos embarques
   */
  const loadContainersForCurrentPage = async () => {
    if (!companyStore.selectedCompanyId) return;
    
    try {
      const start = (currentPage.value - 1) * pageSize.value;
      const end = start + pageSize.value;
      const currentShipments = shipments.value.slice(start, end);
      
      const containersPromises = currentShipments.map(async (shipment: any) => {
        // Verificar si ya están cargados
        if (shipmentsContainers.value[shipment.id]) {
          return null;
        }
        
        const containers = await shipmentService.getContainers(
          shipment.id,
          companyStore.selectedCompanyId
        );
        return { shipmentId: shipment.id, containers };
      });
      
      const results = await Promise.all(containersPromises);
      
      const updated = { ...shipmentsContainers.value };
      results.forEach(result => {
        if (result) {
          updated[result.shipmentId] = result.containers;
        }
      });
      shipmentsContainers.value = updated;
            
    } catch (error) {
      console.error('❌ [useShipments] Error cargando containers:', error);
    }
  };

  // Listener de la colección completa de embarques. Se guarda la función de
  // cierre porque antes se descartaba: cada useShipments() (página de embarques,
  // cabecera del detalle) dejaba un listener vivo para siempre y la pestaña
  // terminaba en "Out of Memory" tras horas de uso.
  let unsubscribeRealtime: (() => void) | null = null;

  const stopRealtimeListener = () => {
    if (unsubscribeRealtime) {
      unsubscribeRealtime();
      unsubscribeRealtime = null;
    }
  };

  // Escucha cambios en la colección para invalidar el caché de la lista.
  //
  // Antes el listener era un onSnapshot sobre la colección completa: Firestore
  // bajaba y mantenía vivos TODOS los documentos de la compañía (10.924 en TCC)
  // solo para leer snapshot.size. Y como el conteo cambiaba con cada embarque
  // nuevo, un watch volvía a bajar la colección entera por fetchShipments.
  //
  // Ahora se observa únicamente el embarque con el lastUpdate más reciente: cada
  // creación o edición lo desplaza y dispara el snapshot con un solo documento.
  // Un borrado hecho desde otra sesión no lo mueve, así que esa invalidación
  // llega con el siguiente cambio; los borrados propios ya refrescan a mano.
  const setupRealtimeListener = () => {
    stopRealtimeListener();
    if (!companyStore.selectedCompanyId) return;
    
    const shipmentsRef = collection(db, 'Companies', companyStore.selectedCompanyId, 'shipments');
    const latestChangeQuery = query(shipmentsRef, orderBy('lastUpdate', 'desc'), limit(1));
    
    unsubscribeRealtime = onSnapshot(latestChangeQuery, () => {
      // Solo invalidar caché, no refrescar automáticamente: quien necesite la
      // lista fresca la pide (refreshShipments) y encuentra el caché vacío.
      simpleCache.clear(`shipments_${companyStore.selectedCompanyId}`);

      // Señal de invalidación para BigQuery mode
      dataInvalidatedSignal.value++;
    });
  };


  // Configurar el listener cuando cambie la compañía
  watch(() => companyStore.selectedCompanyId, (newCompanyId) => {
    if (newCompanyId) {
      setupRealtimeListener();
    } else {
      stopRealtimeListener();
    }
  }, { immediate: true });

  // Cerrar el listener cuando muere el componente/store que creó el composable
  if (getCurrentScope()) {
    onScopeDispose(stopRealtimeListener);
  }


  return {
    shipments,
    shipmentsContainers,
    shipmentsNotifications,
    shipmentsItems,
    shipmentsCustomsDeclarations,
    isLoading,
    error,
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    fetchShipments,
    setPage,
    loadAllContainers,
    loadAllNotifications,
    loadAllItems,
    loadAllCustomsDeclarations,
    loadContainersForCurrentPage,
    dataInvalidatedSignal,
    invalidateContainersCache: () => {
      if (companyStore.selectedCompanyId) {
        simpleCache.clear(`containers_${companyStore.selectedCompanyId}`);
      }
    },
  };
}